import React, { useState } from 'react';

const HomePage: React.FC = () => {
  const [selectedService, setSelectedService] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    zip: '',
    source: '',
    message: ''
  });

  const galleryImages = [
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/c1e805967fec2a133d771ea96697dee7.jpeg',
      alt: 'Kitchen Transformation'
    },
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/cf3706bd317650eb352aaecb4ab57ca7.jpeg',
      alt: 'Bathroom Renovation',
      position: 'object-center'
    },
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/245c449ec90e50d182f187649ab6ddfb.jpeg',
      alt: 'Outdoor Kitchen'
    },
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/be04d9bf9b41a40f4e87dd1e08b20e3d.jpeg',
      alt: 'Kitchen Island'
    },
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/f252c12753aa75b27b6351d096ed98a4.jpeg',
      alt: 'Flooring Installation',
      position: 'object-bottom'
    },
    {
      src: 'https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/fe6466602611c5cbb3d98bae6b9a4d44.jpeg',
      alt: 'Tile Work',
      position: 'object-center'
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceSelect = (service: string) => {
    setSelectedService(service);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
    });
    submitData.append('service', selectedService);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4d4g1u820p5j87gak4g', {
        method: 'POST',
        body: submitData
      });
      
      if (response.ok) {
        alert('Thank you! Your message has been submitted successfully.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          zip: '',
          source: '',
          message: ''
        });
        setSelectedService('');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/b58423ed8dbec69ff13f877be134fa33/63ba074d79add3c20cb2474f0334c62b.png"
                alt="1st Time Done Right Kitchen & Bath"
                className="h-24 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer">Services</a>
              <a href="#gallery" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer">Gallery</a>
              <a href="#about" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer">About</a>
              <a href="#contact" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer">Contact</a>
            </nav>
            <div className="flex items-center space-x-4">
              <a href="tel:+19107410136" className="text-red-600 font-semibold hover:text-red-700 transition-colors cursor-pointer">
                (910) 741-0136
              </a>
              <a href="#contact" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" 
               style={{
                 backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/61a75c7cf5547bb312549930e0a9da8e.jpg')`
               }}>
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Your Kitchen & Bath<br />
            <span className="text-red-500">Transformation Experts</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Custom cabinetry, luxury countertops, and premium tile work. 
            We take pride in every project with attention to detail and commitment to quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="#contact" className="bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap">
              Contact Us
            </a>
            <a href="#gallery" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              View Our Work
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">Committed to quality and fairness in every project</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Experienced Team</h3>
              <p className="text-gray-600">Experienced installers and builders</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Constant Communication</h3>
              <p className="text-gray-600">We keep you informed throughout the process</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Full Service</h3>
              <p className="text-gray-600">From start to finish, we handle it all</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Welcome to 1st Time Done Right Kitchen & Bath
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  We promise to take pride in each client's Home Improvement project, 
                  paying special attention to detail with constant communication and customer satisfaction.
                </p>
                <p>
                  Committed to quality and fairness, we can be there for every aspect of the job from start to finish. 
                  Our experienced installers and builders ensure your project is completed to the highest standards.
                </p>
                <p>
                  Whether you're looking for custom cabinetry, luxury countertops, or premium tile work, 
                  we have the expertise and dedication to bring your vision to life.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20kitchen%20renovation%20team%20installing%20custom%20cabinets%20and%20granite%20countertops%20in%20a%20modern%20home%2C%20showing%20skilled%20craftsmen%20at%20work%20with%20tools%20and%20materials%20in%20a%20clean%2C%20organized%20workspace&width=600&height=400&seq=about-team-1&orientation=landscape"
                alt="Our experienced team at work"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">What We Offer</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From custom cabinetry to luxury finishes, we provide comprehensive kitchen and bath solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20outdoor%20kitchen%20with%20custom%20weather-resistant%20cabinetry%2C%20stainless%20steel%20appliances%2C%20and%20granite%20countertops%20in%20an%20elegant%20backyard%20setting%20with%20professional%20lighting%20and%20landscaping&width=400&height=250&seq=outdoor-cabinets-1&orientation=landscape"
                alt="Outdoor Cabinetry"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Outdoor Cabinetry</h3>
                <p className="text-gray-600 mb-4">Weather-resistant custom cabinetry for your outdoor kitchen and entertainment areas.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20kitchen%20featuring%20high-end%20custom%20cabinetry%20with%20premium%20wood%20finishes%2C%20soft-close%20drawers%2C%20and%20elegant%20hardware%20in%20a%20sophisticated%20modern%20home%20interior&width=400&height=250&seq=luxury-cabinets-1&orientation=landscape"
                alt="Luxury Cabinetry"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Luxury Quality Cabinetry</h3>
                <p className="text-gray-600 mb-4">Premium custom cabinetry crafted with the finest materials and attention to detail.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Elegant%20display%20of%20premium%20kitchen%20cabinet%20hardware%20including%20brushed%20nickel%20drawer%20pulls%2C%20soft-close%20hinges%2C%20and%20modern%20handles%20properly%20installed%20on%20luxury%20cabinetry%2C%20showcasing%20high-quality%20craftsmanship%20with%20detailed%20close-up%20view%20of%20hardware%20finishes%20and%20installation%20in%20a%20bright%2C%20well-lit%20setting&width=400&height=250&seq=hardware-2&orientation=landscape"
                alt="Premium Hardware"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Top of the Line Hardware</h3>
                <p className="text-gray-600 mb-4">Premium hinges, drawer slides, and handles that combine beauty with functionality.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Stunning%20kitchen%20island%20with%20selective%20quartz%20and%20granite%20countertops%20showing%20beautiful%20natural%20stone%20patterns%20and%20professional%20edge%20work%20in%20elegant%20lighting&width=400&height=250&seq=countertops-1&orientation=landscape"
                alt="Quartz and Granite Countertops"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Selective Quartz & Granite Countertops</h3>
                <p className="text-gray-600 mb-4">Beautiful, durable countertops in a variety of colors and patterns.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20home%20interior%20showcasing%20various%20premium%20flooring%20options%20including%20engineered%20hardwood%2C%20luxury%20vinyl%20planking%2C%20and%20laminate%20in%20a%20bright%2C%20well-lit%20space&width=400&height=250&seq=flooring-1&orientation=landscape"
                alt="Premium Flooring"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Premium Flooring</h3>
                <p className="text-gray-600 mb-4">Engineered Hardwood, Hardwood, Luxury Vinyl Planking & Laminate options.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=Elegant%20bathroom%20featuring%20Onyx%20Collection%20fixtures%2C%20porcelain%20and%20ceramic%20tile%20work%20with%20sophisticated%20patterns%20and%20premium%20appliances%20in%20a%20luxury%20home%20setting&width=400&height=250&seq=tile-appliances-1&orientation=landscape"
                alt="Tile and Appliances"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Tile & Appliances</h3>
                <p className="text-gray-600 mb-4">Onyx Collection, Porcelain and Ceramic Tile, plus premium appliances.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Work Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the quality and craftsmanship that sets us apart
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className="relative group overflow-hidden rounded-lg shadow-lg cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className={`w-full h-64 object-cover ${image.position || 'object-top'} group-hover:scale-105 transition-transform duration-300`}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-eye-line text-white text-3xl"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-4xl w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors z-10 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 text-white text-4xl w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 text-white text-4xl w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>

          <div 
            className="max-w-6xl max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentImageIndex].src}
              alt={galleryImages[currentImageIndex].alt}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="text-white text-center mt-4 text-lg">
              {galleryImages[currentImageIndex].alt}
            </div>
            <div className="text-white/70 text-center mt-2">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Real feedback from satisfied customers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "I hired Robin and Karen to do a full bathroom remodel in my guest bathroom. Their price was very fair for the work that needed to be done. They sent progress pictures if I was working. They were on time and communicated very well. They know what they are doing and went the extra mile to make sure everything was done extra well. I would definitely hire them again and would highly recommend."
              </p>
              <div className="font-semibold text-gray-900">Ernest</div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "Painted kitchen cabinets, took their time. Project was done in a timely manner. They kept us in the loop throughout the entire process. Would definitely recommend!! Before & after photos attached."
              </p>
              <div className="font-semibold text-gray-900">Bonnie Sheridanc</div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "I can't sing enough praises about 1st Time Done Right. Robin and Karen have a passion that's a breath of fresh air. The entire process was perfect and I will absolutely hire them again for future projects. If you need a general contractor, hire these guys and just sit back and watch the perfection unfold."
              </p>
              <div className="font-semibold text-gray-900">Michael Moore</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Contact Us</h2>
              <p className="text-xl text-gray-600 mb-8">
                Ready to transform your kitchen or bath? Contact us today for a free consultation.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Phone</div>
                    <a href="tel:+19107410136" className="text-red-600 hover:text-red-700 cursor-pointer">(910) 741-0136</a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Address</div>
                    <div className="text-gray-600">1064 NC-210 Unit 3<br />Sneads Ferry, NC 28460</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Business Hours</div>
                    <div className="text-gray-600">Monday-Friday: 9AM-5PM</div>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3267.8!2d-77.4!3d34.55!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89a9f0e0e0e0e0e0%3A0x0!2s1064%20NC-210%20Unit%203%2C%20Sneads%20Ferry%2C%20NC%2028460!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Business Location Map"
                ></iframe>
              </div>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-lg">
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <h3 className="text-2xl font-semibold mb-6">Choose a Service *</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    'Custom Cabinetry',
                    'Countertops',
                    'Flooring',
                    'Tile Work',
                    'Outdoor Kitchen',
                    'Full Renovation'
                  ].map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => handleServiceSelect(service)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        selectedService === service
                          ? 'border-red-600 bg-red-50 text-red-600'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-red-300'
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>

                <input type="hidden" name="to" value="capedens2015@gmail.com" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-8"
                  >
                    <option value="">Select one...</option>
                    <option value="Search Engine">Search Engine (Google, Bing, etc.)</option>
                    <option value="Social Media">Social Media (Facebook, Instagram, etc.)</option>
                    <option value="Referral">Recommended by friend or colleague</option>
                    <option value="Advertisement">Advertisement</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <div className="text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-4 px-6 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                1st Time Done Right Kitchen & Bath
              </h3>
              <p className="text-gray-300 mb-4">
                Your trusted partner for custom cabinetry, luxury countertops, and premium home improvements.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Our Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">Custom Cabinetry</a></li>
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">Countertops</a></li>
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">Flooring</a></li>
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">Tile Work</a></li>
                <li><a href="#services" className="hover:text-white transition-colors cursor-pointer">Outdoor Kitchens</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <div className="space-y-2 text-gray-300">
                <p><i className="ri-phone-line mr-2"></i>(910) 741-0136</p>
                <p><i className="ri-time-line mr-2"></i>Monday-Friday: 9AM-5PM</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; 2024 1st Time Done Right Kitchen & Bath. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
